import React from 'react'
import { Box, Card, Chip, LinearProgress, Stack, Button, Tooltip } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import FlagIcon from '@mui/icons-material/Flag'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WTypography from '../../../Components/whiteTypography'

/**
 * LAP — Sections visuelles du « Conseiller en Organisation ».
 * Miroir de `TimeAdvisorSections` (ConseillerTemps), regroupées dans un seul
 * fichier (le module ne compte que quelques sections).
 *
 *   - OrgScoreCard       : score global 0-100 + note + décomposition par dimension
 *   - OrgDiagnosticPanel : forces / faiblesses / risques + dimensions non évaluées
 *   - OrgBreakdownPanel  : répartition par domaine (tâches / projets / listes)
 *   - OrgActionsPanel    : liste d'actions prioritaires
 */

const SCORE_COLORS = {
  good: '#4ade80',
  warning: '#fbbf24',
  critical: '#f87171',
}

// Gris neutre pour les dimensions « Non évalué » (aucune donnée).
const NEUTRAL_COLOR = '#94a3b8'

function scoreColor(score) {
  if (score == null) return NEUTRAL_COLOR
  if (score >= 70) return SCORE_COLORS.good
  if (score >= 45) return SCORE_COLORS.warning
  return SCORE_COLORS.critical
}

// ─────────────────────────────────────────────────────────────────────────
// Score
// ─────────────────────────────────────────────────────────────────────────

export function OrgScoreCard({ score, userFirstName, metrics }) {
  const { t } = useTranslation('layout')
  const color = scoreColor(score.global)

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #0f1d3a 0%, #1a2f5e 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2.5, md: 4 },
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
            {t('conseillerOrganisation.score.overline', { defaultValue: 'Diagnostic organisation' })}
          </WTypography>
          <WTypography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerOrganisation.score.title', {
              defaultValue: `Comment vous vous organisez, ${userFirstName || ''}`,
              name: userFirstName,
            })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 1 }}>
            {t('conseillerOrganisation.score.subtitle', {
              defaultValue:
                'Une note synthétique calculée sur vos habitudes, l’équilibre de vos domaines, l’avancement de vos tâches et le pilotage par objectifs.',
            })}
          </WTypography>
          {typeof score.evaluatedCount === 'number' &&
            score.evaluatedCount < (score.totalCount || 4) && (
              <WTypography
                variant="caption"
                sx={{ color: '#a0b4d6 !important', mt: 1, display: 'block' }}
              >
                {t('conseillerOrganisation.score.coverage', {
                  defaultValue: `Score calculé sur ${score.evaluatedCount}/${
                    score.totalCount || 4
                  } dimensions — les autres sont « Non évalué » faute de données. Complétez votre todo pour les activer.`,
                  evaluated: score.evaluatedCount,
                  total: score.totalCount || 4,
                })}
              </WTypography>
            )}
          <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5}>
            {typeof metrics?.totalTasks === 'number' && (
              <Chip
                size="small"
                label={t('conseillerOrganisation.score.tasksChip', {
                  defaultValue: `${metrics.totalTasks} tâche(s)`,
                  count: metrics.totalTasks,
                })}
                sx={{ bgcolor: 'rgba(96,165,250,0.15)', color: '#a8c1f0', fontWeight: 700 }}
              />
            )}
            {typeof metrics?.projetsCount === 'number' && (
              <Chip
                size="small"
                label={t('conseillerOrganisation.score.projectsChip', {
                  defaultValue: `${metrics.projetsCount} projet(s)`,
                  count: metrics.projetsCount,
                })}
                sx={{ bgcolor: 'rgba(169,135,255,0.15)', color: '#cdbcff', fontWeight: 700 }}
              />
            )}
            {typeof metrics?.domainsCovered === 'number' && metrics.domainsCovered > 0 && (
              <Chip
                size="small"
                label={t('conseillerOrganisation.score.domainsChip', {
                  defaultValue: `${metrics.domainsCovered} domaine(s)`,
                  count: metrics.domainsCovered,
                })}
                sx={{ bgcolor: 'rgba(124,198,178,0.15)', color: '#9ee0cf', fontWeight: 700 }}
              />
            )}
          </Stack>
        </Box>
        <ScoreCircle value={score.global} grade={score.grade} color={color} t={t} />
      </Box>

      <WTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#dbe5f7 !important' }}>
        {t('conseillerOrganisation.score.dimensionsTitle', { defaultValue: 'Détail par dimension' })}
      </WTypography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        gap={2}
      >
        {score.dimensions.map((d) => (
          <DimensionRow key={d.key} dim={d} t={t} />
        ))}
      </Box>
    </Card>
  )
}

function ScoreCircle({ value, grade, color, t }) {
  const size = 160
  const stroke = 10
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const hasValue = typeof value === 'number' && Number.isFinite(value)
  const offset = circumference * (1 - (hasValue ? value : 0) / 100)

  return (
    <Box position="relative" width={size} height={size}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <Box
        position="absolute"
        top={0}
        left={0}
        width={size}
        height={size}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <WTypography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
          {hasValue ? value : '—'}
        </WTypography>
        {hasValue && grade && grade !== '—' ? (
          <Chip
            label={grade}
            size="small"
            sx={{
              mt: 0.5,
              bgcolor: `${color}22`,
              color: `${color}`,
              fontWeight: 800,
              height: 22,
            }}
          />
        ) : (
          <WTypography sx={{ fontSize: 13, color: '#a0b4d6 !important' }}>
            {hasValue
              ? t('conseillerOrganisation.score.outOf100', { defaultValue: '/ 100' })
              : t('conseillerOrganisation.score.noData', { defaultValue: 'non évalué' })}
          </WTypography>
        )}
      </Box>
    </Box>
  )
}

function DimensionRow({ dim, t }) {
  const available = dim.available !== false
  const color = available ? scoreColor(dim.score) : NEUTRAL_COLOR
  const statusColor = available ? SCORE_COLORS[dim.status] || NEUTRAL_COLOR : NEUTRAL_COLOR
  const notEvaluatedLabel = t('conseillerOrganisation.score.notEvaluated', {
    defaultValue: 'Non évalué',
  })
  const tooltipText = available
    ? [dim.detail, dim.dataSource].filter(Boolean).join(' ')
    : dim.emptyHint || dim.detail

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          cursor: 'help',
          transition: 'all .2s',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', transform: 'translateY(-2px)' },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <WTypography variant="body2" sx={{ fontWeight: 600 }}>
            {dim.label}
          </WTypography>
          <WTypography
            variant="body2"
            sx={{
              color: `${color} !important`,
              fontWeight: 700,
              fontSize: available ? undefined : 12,
            }}
          >
            {available ? dim.score : notEvaluatedLabel}
          </WTypography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={available ? dim.score : 0}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
          <Box width={6} height={6} borderRadius="50%" bgcolor={statusColor} />
          <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
            {t('conseillerOrganisation.score.weightLabel', {
              defaultValue: `pondération ${Math.round(dim.weight * 100)} %`,
              weight: Math.round(dim.weight * 100),
            })}
          </WTypography>
        </Box>
        {(dim.dataSource || dim.emptyHint) && (
          <WTypography
            variant="caption"
            sx={{ color: '#8294b5 !important', display: 'block', mt: 0.75, lineHeight: 1.4 }}
          >
            {available ? dim.dataSource : dim.emptyHint}
          </WTypography>
        )}
      </Box>
    </Tooltip>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Diagnostic
// ─────────────────────────────────────────────────────────────────────────

export function OrgDiagnosticPanel({ diagnostic, recommendations = [] }) {
  const { t } = useTranslation('layout')
  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerOrganisation.diagnostic.title', { defaultValue: 'Forces, faiblesses, risques' })}
      </WTypography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr 1fr' }} gap={2}>
        <DiagColumn
          icon={<CheckCircleOutlineIcon sx={{ color: SCORE_COLORS.good }} />}
          color={SCORE_COLORS.good}
          title={t('conseillerOrganisation.diagnostic.strengths', { defaultValue: 'Forces' })}
          items={diagnostic.strengths}
          empty={t('conseillerOrganisation.diagnostic.strengthsEmpty', {
            defaultValue: 'Pas encore de point fort identifié — ça vient avec la pratique.',
          })}
        />
        <DiagColumn
          icon={<WarningAmberIcon sx={{ color: SCORE_COLORS.warning }} />}
          color={SCORE_COLORS.warning}
          title={t('conseillerOrganisation.diagnostic.weaknesses', { defaultValue: 'Faiblesses' })}
          items={diagnostic.weaknesses}
          empty={t('conseillerOrganisation.diagnostic.weaknessesEmpty', {
            defaultValue: 'Aucune zone moyenne, c’est rare et excellent.',
          })}
        />
        <DiagColumn
          icon={<ReportProblemOutlinedIcon sx={{ color: SCORE_COLORS.critical }} />}
          color={SCORE_COLORS.critical}
          title={t('conseillerOrganisation.diagnostic.risks', { defaultValue: 'Risques' })}
          items={diagnostic.risks}
          empty={t('conseillerOrganisation.diagnostic.risksEmpty', {
            defaultValue: 'Pas de risque critique pour le moment. Continuez à monitorer.',
          })}
        />
      </Box>

      {diagnostic.notEvaluated && diagnostic.notEvaluated.length > 0 && (
        <Box
          mt={2.5}
          sx={{
            p: 1.75,
            borderRadius: 2,
            bgcolor: 'rgba(148,163,184,0.08)',
            border: '1px solid rgba(148,163,184,0.25)',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <HelpOutlineIcon sx={{ color: NEUTRAL_COLOR, fontSize: 18 }} />
            <WTypography variant="subtitle2" sx={{ fontWeight: 700, color: `${NEUTRAL_COLOR} !important` }}>
              {t('conseillerOrganisation.diagnostic.notEvaluated', {
                defaultValue: 'Dimensions non évaluées',
              })}
            </WTypography>
          </Box>
          <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', display: 'block', mb: 1 }}>
            {t('conseillerOrganisation.diagnostic.notEvaluatedHint', {
              defaultValue:
                'Faute de données, ces dimensions ne comptent pas dans le score et n’apparaissent ni en force ni en risque. Renseignez-les pour les activer.',
            })}
          </WTypography>
          <Stack gap={0.75}>
            {diagnostic.notEvaluated.map((it) => (
              <Box key={it.key}>
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {it.title}
                </WTypography>
                <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                  {it.detail}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {recommendations.length > 0 && (
        <Box mt={3}>
          <WTypography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('conseillerOrganisation.diagnostic.recoTitle', {
              defaultValue: 'Recommandations personnalisées',
            })}
          </WTypography>
          <Stack gap={1}>
            {recommendations.map((r, i) => (
              <Box
                key={i}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(126,156,219,0.10)',
                  border: '1px solid rgba(126,156,219,0.25)',
                }}
              >
                <WTypography variant="body2" sx={{ color: '#dbe5f7 !important' }}>
                  {r}
                </WTypography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  )
}

function DiagColumn({ icon, color, title, items, empty }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: `1px solid ${color}33`,
        minHeight: 140,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={1.25}>
        {icon}
        <WTypography variant="subtitle2" sx={{ fontWeight: 700, color: `${color} !important` }}>
          {title}
        </WTypography>
      </Box>
      {items.length === 0 ? (
        <WTypography variant="body2" sx={{ color: '#a0b4d6 !important', fontStyle: 'italic' }}>
          {empty}
        </WTypography>
      ) : (
        <Stack gap={0.75}>
          {items.map((it) => (
            <Box key={it.key}>
              <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                {it.title}
              </WTypography>
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                {it.detail}
              </WTypography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Répartition par domaine
// ─────────────────────────────────────────────────────────────────────────

export function OrgBreakdownPanel({ breakdown }) {
  const { t } = useTranslation('layout')
  const entries = breakdown || []
  if (entries.length === 0) return null

  const maxCount = entries.reduce((m, e) => Math.max(m, e.count), 0) || 1

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <WTypography variant="h6" sx={{ fontWeight: 700 }}>
          {t('conseillerOrganisation.breakdown.title', {
            defaultValue: 'Répartition par domaine',
          })}
        </WTypography>
        <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
          {t('conseillerOrganisation.breakdown.hint', {
            defaultValue: 'Tâches, projets et listes rattachés à chaque domaine de vie.',
          })}
        </WTypography>
      </Box>

      <Stack gap={1.5}>
        {entries.map((row) => (
          <Box key={row.domain}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color }} />
                <WTypography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.domain}
                </WTypography>
              </Box>
              <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                {t('conseillerOrganisation.breakdown.countPercent', {
                  defaultValue: `${row.count} · ${row.percent}%`,
                  count: row.count,
                  percent: row.percent,
                })}
              </WTypography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.round((row.count / maxCount) * 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': { bgcolor: row.color, borderRadius: 4 },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Actions prioritaires
// ─────────────────────────────────────────────────────────────────────────

const IMPACT_COLORS = {
  high: '#f87171',
  medium: '#fbbf24',
  low: '#4ade80',
}

export function OrgActionsPanel({ actions }) {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  if (!actions || actions.length === 0) return null

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: { xs: 2, md: 3 },
        color: 'white',
      }}
    >
      <WTypography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {t('conseillerOrganisation.actions.title', { defaultValue: 'Actions prioritaires' })}
      </WTypography>
      <Stack gap={1.5}>
        {actions.map((a) => (
          <Box
            key={a.id}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                <FlagIcon sx={{ color: IMPACT_COLORS[a.impact] || '#94a3b8', fontSize: 18 }} />
                <WTypography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {a.title}
                </WTypography>
                <Chip
                  size="small"
                  label={t(`conseillerOrganisation.actions.impact.${a.impact}`, {
                    defaultValue: a.impact,
                  })}
                  sx={{
                    bgcolor: `${IMPACT_COLORS[a.impact] || '#94a3b8'}22`,
                    color: IMPACT_COLORS[a.impact] || '#94a3b8',
                    fontWeight: 700,
                    height: 22,
                  }}
                />
                {a.effortMinutes && (
                  <Chip
                    size="small"
                    icon={<AccessTimeIcon sx={{ color: '#a0b4d6 !important' }} />}
                    label={t('conseillerOrganisation.actions.effortMinutes', {
                      defaultValue: `${a.effortMinutes} min`,
                      minutes: a.effortMinutes,
                    })}
                    sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#dbe5f7', height: 22 }}
                  />
                )}
              </Box>
              <WTypography variant="body2" sx={{ color: '#c1d0eb !important' }}>
                {a.description}
              </WTypography>
            </Box>
            {a.route && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(a.route)}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#a8c1f0',
                  borderColor: 'rgba(126,156,219,0.45)',
                  '&:hover': { bgcolor: 'rgba(126,156,219,0.12)' },
                }}
              >
                {t('conseillerOrganisation.actions.cta', { defaultValue: 'Y aller' })}
              </Button>
            )}
          </Box>
        ))}
      </Stack>
    </Card>
  )
}
